/**
 * §onboarding-activation (Task 6) — getting-started checklist client.
 *
 * Reads the per-tenant checklist whose every step is computed server-side from
 * REAL persisted state (no `always:true` fakes), so the UI can render honest
 * checkmarks for a freshly-onboarded owner.
 */
import { api } from "@/lib/api";

export interface OnboardingChecklistStep {
  id: string;
  label: string;
  done: boolean;
}

export interface OnboardingChecklist {
  steps: OnboardingChecklistStep[];
  completed: number;
  total: number;
  percent: number;
  next_step_id: string | null;
  complete: boolean;
}

export async function getOnboardingChecklist(): Promise<OnboardingChecklist> {
  return api<OnboardingChecklist>("/api/admin/onboarding/checklist");
}

/**
 * §onboarding.go-live — the agent's publish state on customer channels.
 * A freshly-provisioned tenant starts paused (`configured=true, published=false`):
 * the agent stays silent on real channels until the owner clicks Go live. A tenant
 * that predates the feature has `configured=false` (grandfathered → published).
 */
export interface AgentPublishStatus {
  tenant_id: string;
  published: boolean;
  configured: boolean;
  published_at?: number | null;
  published_by?: string | null;
}

export async function getAgentPublishStatus(): Promise<AgentPublishStatus> {
  return api<AgentPublishStatus>("/api/admin/agent/publish-status");
}

/** Flip the agent live (default) or pause it on customer channels. */
export async function setAgentGoLive(published = true): Promise<AgentPublishStatus> {
  return api<AgentPublishStatus>("/api/admin/agent/go-live", {
    method: "POST",
    body: { published },
  });
}

export interface TelegramConnectResult {
  ok: boolean;
  channel: string;
  bot_username: string;
  webhook_registered: boolean;
}

/**
 * §onboarding-activation (Task 4) — connect Telegram in one step: the server
 * validates the bot token live via getMe, saves it, and registers the webhook.
 *
 * §onboarding-channels — call with NO argument to connect from the token the
 * Owner captured in the onboarding wizard (sealed server-side; the endpoint
 * decrypts it, never returning it to the browser). Pass a token to connect a
 * freshly-pasted one.
 */
export async function connectTelegram(
  botToken?: string,
): Promise<TelegramConnectResult> {
  return api<TelegramConnectResult>("/api/admin/channels/telegram/connect", {
    method: "POST",
    body: botToken ? { bot_token: botToken } : {},
  });
}

/** Browser-safe view of which channels the Owner captured creds for in the
 * wizard: presence flags + non-secret hints only (never the secret).
 * §onboarding-assist — `assist_requested` marks channels the Owner asked XPAND
 * to set up (present is false for those: there are no creds on file). */
export interface CapturedChannels {
  telegram?: { present: boolean };
  whatsapp?: { present: boolean; phone_number_id?: string; assist_requested?: boolean };
  instagram?: { present: boolean; assist_requested?: boolean };
  voice?: { present: boolean; openai_project_id?: string };
}

export async function getCapturedChannels(): Promise<CapturedChannels> {
  const r = await api<{ captured: CapturedChannels }>("/api/admin/channels/captured");
  return r.captured || {};
}

export interface WhatsAppConnectResult {
  ok: boolean;
  channel: string;
  display_phone_number: string;
  verified_name: string;
  /** Handshake nonce to paste into Meta's webhook config (not a secret). */
  verify_token: string;
  /** Inbound webhook Callback URL to paste into Meta (empty if no public HTTPS base). */
  webhook_url: string;
  /** True when no public HTTPS base is configured, so the webhook is pending. */
  webhook_pending: boolean;
}

/**
 * §onboarding-activation — connect WhatsApp Cloud API in one step: the server
 * validates the phone_number_id + access_token live via a Graph phone-node GET,
 * saves them (auto-mints the verify_token), and returns the verify_token +
 * webhook URL to paste into the Meta App dashboard.
 */
export async function connectWhatsApp(args?: {
  phoneNumberId: string;
  accessToken: string;
  appSecret?: string;
}): Promise<WhatsAppConnectResult> {
  // §onboarding-channels — call with NO args to connect from the wizard-captured
  // creds (decrypted server-side, never returned to the browser).
  const body: Record<string, string> = {};
  if (args) {
    body.phone_number_id = args.phoneNumberId;
    body.access_token = args.accessToken;
    if (args.appSecret && args.appSecret.trim()) body.app_secret = args.appSecret.trim();
  }
  return api<WhatsAppConnectResult>("/api/admin/channels/whatsapp/connect", {
    method: "POST",
    body,
  });
}

export interface InstagramConnectResult {
  ok: boolean;
  channel: string;
  ig_id: string;
  username: string;
  verify_token: string;
  webhook_url: string;
  webhook_pending: boolean;
}

/** Connect Instagram DMs. No args → connect from wizard-captured token. */
export async function connectInstagram(args?: {
  accessToken?: string;
  appSecret?: string;
  igId?: string;
}): Promise<InstagramConnectResult> {
  const body: Record<string, string> = {};
  if (args?.accessToken) body.access_token = args.accessToken;
  if (args?.appSecret) body.app_secret = args.appSecret;
  if (args?.igId) body.ig_id = args.igId;
  return api<InstagramConnectResult>("/api/admin/channels/instagram/connect", {
    method: "POST",
    body,
  });
}

export interface VoiceConnectResult {
  ok: boolean;
  channel: string;
  /** "ucmrc" (PBX bridge — call-ready) or "trunk" (direct SIP/Twilio). */
  method: string;
  /** True for UCM/PBX. For the trunk route, false until a real call confirms
   *  the carrier side (origination + IP allowlist) — see routing_ready. */
  call_ready: boolean;
  /** Trunk: our platform-side DID→tenant routing is wired; carrier steps remain. */
  routing_ready?: boolean;
  note: string;
  extension?: string;        // ucmrc
  did?: string;              // trunk
  sip_ingress_uri?: string;  // trunk — point the carrier/Twilio trunk here
  sip_user?: string;         // trunk — auth user for the trunk
}

/**
 * Connect voice. No args → connect from the wizard-captured creds (the server
 * dispatches UCM/PBX vs SIP-trunk by which creds are present). Both answer with
 * our Grok agent via our bridge.
 */
export async function connectVoice(args?: {
  ucmrcHost?: string;
  ucmrcExtension?: string;
  ucmrcPassword?: string;
  ucmrcInboundDid?: string;
  trunkDid?: string;
}): Promise<VoiceConnectResult> {
  const body: Record<string, string> = {};
  if (args?.ucmrcHost) body.ucmrc_host = args.ucmrcHost;
  if (args?.ucmrcExtension) body.ucmrc_extension = args.ucmrcExtension;
  if (args?.ucmrcPassword) body.ucmrc_password = args.ucmrcPassword;
  if (args?.ucmrcInboundDid) body.ucmrc_inbound_did = args.ucmrcInboundDid;
  if (args?.trunkDid) body.trunk_did = args.trunkDid;
  return api<VoiceConnectResult>("/api/admin/channels/voice/connect", {
    method: "POST",
    body,
  });
}

export interface WebConnectResult {
  ok: boolean;
  channel: string;
  /** Public widget key (safe to embed in the page). */
  api_key: string;
  widget_url: string;
  /** The one-line <script> snippet to paste before </body>. */
  snippet: string;
}

/**
 * §onboarding-channels — connect the public web-chat widget in one step: the
 * server mints a public key, enables the ingress, records channel_connected, and
 * returns the embed snippet. No creds / no external enrollment.
 */
export async function connectWeb(): Promise<WebConnectResult> {
  return api<WebConnectResult>("/api/admin/channels/web/connect", {
    method: "POST",
    body: {},
  });
}

/**
 * §onboarding-activation — record that the owner saw a REAL agent preview (the
 * Launchpad "Ask your agent" aha). Idempotent server-side per tenant; feeds the
 * activation funnel. Fire-and-forget — callers may ignore failures.
 */
export async function recordAgentPreviewed(): Promise<void> {
  await api<{ ok: boolean }>("/api/admin/onboarding/previewed", {
    method: "POST",
    body: {},
  });
}
